/**
 * FarmersPulseBar — thin ribbon rendered above every page linking to
 * FarmersPulse.com (the sister growers' hub).
 *
 * • Sticky at the very top (above the SocialPulse header, SisterSitesBar,
 *   the FoodPulse TopNav, and every other bit of chrome).
 * • Dismissable per browser via localStorage (`fp_farmerspulse_dismissed`).
 * • Hidden on `/admin/cms/*`, `/install` and mobile-app routes to keep
 *   focused views focused.
 */
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { ExternalLink, Tractor, X } from "lucide-react";

const LS_KEY = "fp_farmerspulse_dismissed";

export default function FarmersPulseBar() {
  const { pathname } = useLocation();
  const [dismissed, setDismissed] = useState(true); // hydrate to true so
                                                    // the bar can't flash
                                                    // before the LS read.
  useEffect(() => {
    try { setDismissed(localStorage.getItem(LS_KEY) === "1"); } catch { setDismissed(false); }
  }, []);

  const hideOn = /^\/(admin\/cms|install|m\/)/.test(pathname);
  if (hideOn || dismissed) return null;

  const close = () => {
    setDismissed(true);
    try { localStorage.setItem(LS_KEY, "1"); } catch { /* ignore */ }
  };

  return (
    <div
      data-testid="farmerspulse-bar"
      className="relative z-[60] bg-gradient-to-r from-amber-500 via-amber-500 to-orange-500 text-white"
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between gap-3 px-4 py-1.5 text-xs sm:text-sm">
        <div className="flex flex-1 items-center justify-center gap-2 sm:justify-start">
          <Tractor className="h-4 w-4 shrink-0" />
          <span className="hidden sm:inline">Growing food yourself?</span>
          <a
            href="https://farmerspulse.com"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="farmerspulse-bar-link"
            className="group inline-flex items-center gap-1 font-semibold underline-offset-2 hover:underline"
          >
            Visit FarmersPulse.com
            <ExternalLink className="h-3.5 w-3.5 opacity-90 transition group-hover:translate-x-0.5" />
          </a>
          <span className="hidden md:inline text-white/80">
            — the growers' hub for markets, seeds and seasonal know-how.
          </span>
        </div>
        <button
          onClick={close}
          data-testid="farmerspulse-bar-dismiss"
          aria-label="Dismiss FarmersPulse link"
          className="shrink-0 rounded-full p-0.5 hover:bg-white/20"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}
